/**
 * "Who's out" — the team-visible availability strip above My Calendar.
 *
 * IMPORTANT: this reads `crm.user_absences` (GET /api/crm/absences), which is
 * team-visible BY DESIGN. It does NOT read personal calendar events — those are
 * the one owner-private table in `crm.*` (RLS `user_id = auth.uid()`, and the
 * calendar API filters to the viewer even for admins), so a colleague's PTO
 * could never have been shown here from the calendar. That privacy contract is
 * untouched: the absence record is a separate, deliberately shareable fact, and
 * this strip is the surface it exists for.
 *
 * Shows who is away today (with when they're back), the next week's absences,
 * and the one action that files a new one.
 */
import { useMemo } from 'react';
import { CalendarCheck, Loader2, UserMinus, UserPlus } from 'lucide-react';
import { UserAbsence } from '../../types';
import { backOnLabel, dayKeyToDate, shortDay, toDayKey } from './AbsenceModal';

interface WhosOutBarProps {
  /** Every absence overlapping the fetched window, network-wide. */
  absences: UserAbsence[];
  /** Today as YYYY-MM-DD. */
  todayKey: string;
  /** The viewer, so their own absence reads "You". */
  currentUserId?: string;
  loading?: boolean;
  /** Absence-load failure — the calendar itself still renders. */
  error?: string | null;
  onMarkOut: () => void;
  onSelect: (absence: UserAbsence) => void;
}

/** How far ahead the "coming up" half looks. */
const LOOKAHEAD_DAYS = 7;

const firstName = (n: string | null | undefined) => (n || '').trim().split(/\s+/)[0] || '';

export default function WhosOutBar({
  absences,
  todayKey,
  currentUserId,
  loading = false,
  error = null,
  onMarkOut,
  onSelect,
}: WhosOutBarProps) {
  const horizon = useMemo(() => {
    const d = dayKeyToDate(todayKey);
    d.setDate(d.getDate() + LOOKAHEAD_DAYS);
    return toDayKey(d);
  }, [todayKey]);

  // Inclusive day strings compare correctly as plain strings (ISO ordering).
  const outToday = useMemo(
    () => absences.filter((a) => a.starts_on <= todayKey && a.ends_on >= todayKey),
    [absences, todayKey]
  );

  const upcoming = useMemo(
    () =>
      absences
        .filter((a) => a.starts_on > todayKey && a.starts_on <= horizon)
        .sort((a, b) => a.starts_on.localeCompare(b.starts_on) || (a.user_name || '').localeCompare(b.user_name || '')),
    [absences, todayKey, horizon]
  );

  const label = (a: UserAbsence) =>
    a.user_id === currentUserId ? 'You' : firstName(a.user_name) || a.user_name || 'Teammate';

  const chipCls =
    'inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:min-h-0 sm:py-1';

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        <UserMinus className="h-3.5 w-3.5" />
        Who's out
      </span>

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {loading && absences.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
          </span>
        ) : error ? (
          <span className="text-xs text-gray-400">Couldn't load who's out — assume everyone is in.</span>
        ) : outToday.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <CalendarCheck className="h-3.5 w-3.5 text-gray-400" />
            Everyone's in today.
          </span>
        ) : (
          outToday.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              title={`${a.user_name || 'Recruiter'} · ${shortDay(a.starts_on)} – ${shortDay(a.ends_on)}${
                a.note ? ` · ${a.note}` : ''
              }`}
              className={chipCls}
            >
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
              <span className="truncate">{label(a)}</span>
              {backOnLabel(a.ends_on) && (
                <span className="font-normal text-gray-400">back {backOnLabel(a.ends_on)}</span>
              )}
            </button>
          ))
        )}

        {upcoming.length > 0 && !error && (
          <span className="inline-flex flex-wrap items-center gap-1.5 border-l border-gray-200 pl-2.5 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <span className="text-gray-400">Coming up:</span>
            {upcoming.slice(0, 4).map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a)}
                title={`${a.user_name || 'Recruiter'} · ${shortDay(a.starts_on)} – ${shortDay(a.ends_on)}`}
                className="rounded px-1 py-0.5 font-medium text-gray-600 underline-offset-2 transition-colors duration-150 hover:bg-gray-100 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {label(a)}{' '}
                <span className="font-normal text-gray-400">
                  {a.starts_on === a.ends_on ? shortDay(a.starts_on) : `${shortDay(a.starts_on)} – ${shortDay(a.ends_on)}`}
                </span>
              </button>
            ))}
            {upcoming.length > 4 && <span className="text-gray-400">+{upcoming.length - 4} more</span>}
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onMarkOut}
        className="inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
      >
        <UserPlus className="h-4 w-4" />
        Mark someone out
      </button>
    </div>
  );
}
